import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { QuantitySelectorV2 } from '@fdha/web-ui-library';

import { getStyles } from './MealItem.styles';

interface MealItem {
  dishId: string;
  menuOptionId: string;
  name: string;
  photoUrl?: string;
  quantity: number;
  mealkitVariantId?: string;
  mealkitVariantIngredientName?: string;
  mealOptionType?: string;
}

interface MealItemProps {
  item: MealItem;
  isReadOnly: boolean;
  hasMultipleVariants?: (menuOptionId: string) => boolean;
  onQuantityChange: (item: MealItem, newQuantity: number) => void;
  onItemClick: (item: MealItem) => void;
}

const MealItemComponent: React.FC<MealItemProps> = ({
  item,
  isReadOnly,
  hasMultipleVariants,
  onQuantityChange,
  onItemClick,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const handleQuantityChange = (newQuantity: number) => {
    onQuantityChange(item, newQuantity);
  };

  const handleItemClick = () => {
    onItemClick(item);
  };

  const showIngredientName =
    item.mealkitVariantIngredientName &&
    hasMultipleVariants?.(item.menuOptionId);

  return (
    <Box
      key={
        item.mealkitVariantId
          ? `${item.menuOptionId}-${item.mealkitVariantId}`
          : item.menuOptionId
      }
      sx={styles.mealItemContainer}
    >
      <Box sx={styles.mealClickArea} onClick={handleItemClick}>
        {/* Meal Image */}
        <Box sx={styles.imageContainer}>
          {item.photoUrl ? (
            <Box
              component="img"
              src={item.photoUrl}
              alt={item.name}
              sx={styles.image}
            />
          ) : (
            <Box sx={styles.imagePlaceholder} />
          )}
        </Box>

        {/* Meal Info */}
        <Box sx={styles.mealInfo}>
          <Typography variant="body1">{item.name}</Typography>
          {showIngredientName && (
            <Typography variant="body2" sx={styles.variantText}>
              with {item.mealkitVariantIngredientName}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Quantity Selector */}
      <QuantitySelectorV2
        value={item.quantity}
        onChange={handleQuantityChange}
        isDisabled={isReadOnly}
      />
    </Box>
  );
};

export default MealItemComponent;
